import { useMemo } from 'react'

import { useOpenWindow, type SectionView } from '@krp/client-runtime'
import {
  PLATFORM_WINDOWS,
  ResourcePickerScreen,
  grantsOf,
  platformMyResources,
  type ResourcePickerActions,
} from '@krp/platform-ui'
import { StatusMessage } from '@krp/ui-kit'

import { useAppDispatch } from '../../app/hooks'
import type { SectionTabs } from '../../app/sectionTabs'
import { SECTION_SERVICES } from '../../composition/sectionServices'
import { useSectionTileNotes } from '../../composition/sectionTileNotes'
import { tileOf } from './resourceTiles'
import { SOURCE_PICKER_ROWS } from './sourcePickerRows'
import { DownSourceSignals } from './SourceHealthSignal'
import { useMyAccess } from './useMyAccess'

/**
 * Выбор источника раздела плитками (универсальный, как в «РМК»): список — «мои ресурсы» с правами окон раздела,
 * плитка — один источник со всеми окнами раздела, которые он умеет. Раздел сам вкладку не открывает: человек
 * кликает на карточку, и тогда открывается окно источника (адрес с rid), а «Назад» возвращает к плиткам.
 * Слова и записи реестра действий раздела — строка таблицы `SOURCE_PICKER_ROWS` (RP-SRC1).
 */
export function SectionResourcePicker({ section, sectionTabs }: { section: SectionView; sectionTabs: SectionTabs }) {
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const access = useMyAccess()
  const row = SOURCE_PICKER_ROWS[section.id]
  const noteOf = useSectionTileNotes(section.id)

  // Службы раздела без ресурса-источника (например, журнал и кассиры) приходят из composition: раздел их вида не знает.
  const services = useMemo(() => SECTION_SERVICES.filter((service) => service.sectionId === section.id && access.data?.api.includes(service.requires) === true), [access.data, section.id])
  const sources = useMemo(() => sectionTabs.resources.map((source) => source.resource), [sectionTabs.resources])
  const tiles = useMemo(
    () =>
      [...sectionTabs.resources.map((source) =>
        tileOf(source.resource, [...(source.labels.length > 1 ? [`Разделы: ${source.labels.join(' · ')}`] : []), ...noteOf(source.resource.id)]),
      ), ...services.map((service) => service.tile)],
    [noteOf, sectionTabs.resources, services],
  )

  const actions = useMemo<ResourcePickerActions>(
    () => ({
      open: (id) => {
        const service = services.find((item) => item.tile.id === id)
        if (service !== undefined) { openWindow(service.window); return }
        const source = sectionTabs.resources.find((item) => item.resource.id === id)
        if (source !== undefined) openWindow(source.kind, { rid: id })
      },
      reload: () => {
        for (const capability of new Set(section.tabs.map((tab) => tab.capability))) {
          void dispatch(platformMyResources.initiate({ capability }, { forceRefetch: true }))
        }
      },
      openResources: () => openWindow(PLATFORM_WINDOWS.resources),
    }),
    [dispatch, openWindow, section.tabs, sectionTabs.resources, services],
  )

  // Раздел объявил выбор плитками, а слов для него здесь нет — это ошибка сборки, а не пустой экран.
  if (row === undefined) {
    return <StatusMessage kind="error" title={`Для раздела «${section.label}» не заданы слова выбора источника`} />
  }
  return (
    <>
      <DownSourceSignals resources={sources} />
      <ResourcePickerScreen
        vm={{ query: sectionTabs.query, tiles, canManageResources: grantsOf(access).registryRead }}
        actions={actions}
        copy={row.copy}
        renderButton={row.renderButton}
      />
    </>
  )
}
